import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Download, ArrowLeft } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import TransactionInvoiceTemplate from "../../component/dashboard-section/payment/transaction-invoice-template";
import MyLoader from "../../component/reusebale/myLoader";
import BaseUrl from "../../constants/BaseUrl";

const TransactionInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceRef = useRef(null);
  
  const { loginSuccess, userCompleteProfile } = useSelector((state) => state.auth);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [leaseDetails, setLeaseDetails] = useState(null);

  // Get transaction data from navigation state or fetch it
  useEffect(() => {
    const fetchData = async () => {
      // Check if data was passed via navigation state
      if (location.state?.transaction && location.state?.leaseDetails) {
        setTransaction(location.state.transaction);
        setLeaseDetails(location.state.leaseDetails);
        setLoading(false);
        return;
      }

      // If no state data, try to get from URL params
      const searchParams = new URLSearchParams(location.search);
      const transactionId = searchParams.get('transactionId');
      const leaseId = searchParams.get('leaseId');

      if (!transactionId || !leaseId) {
        console.error("Missing transaction or lease ID");
        navigate('/dashboard');
        return;
      }

      // Fetch the data
      try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        // Fetch lease details
        const leaseResponse = await fetch(
          `${BaseUrl}api/lease/getLeaseDetails/${leaseId}`,
          requestOptions
        );
        const leaseResult = await leaseResponse.json();

        if (leaseResult.success) {
          setLeaseDetails(leaseResult.data);
        }

        // Fetch transaction details from the lease transactions
        const transactionsResponse = await fetch(
          `${BaseUrl}api/payment-transaction/lease/${leaseId}`,
          requestOptions
        );
        const transactionsResult = await transactionsResponse.json();

        if (transactionsResult.status === "success" && transactionsResult.data) {
          const foundTransaction = transactionsResult.data.find(
            t => t._id === transactionId
          );
          if (foundTransaction) {
            setTransaction(foundTransaction);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        alert("Error loading invoice data");
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location, navigate, loginSuccess?.token]);

  // Handle PDF download
  const handleDownloadInvoice = async () => {
    if (!transaction || !leaseDetails || !userCompleteProfile?.personalInformation) {
      alert("Invoice data not available");
      return;
    }

    setDownloading(true);

    try {
      const element = invoiceRef.current;

      if (element) {
        // Generate canvas from the element with optimized settings
        const canvas = await html2canvas(element, {
          scale: 1.5, // Reduced from 2 to 1.5 for smaller file size while maintaining quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: element.scrollWidth,
          height: element.scrollHeight,
          logging: false,
          imageTimeout: 0,
        });

        // Create PDF
        // Use JPEG with compression instead of PNG for much smaller file size
        const imgData = canvas.toDataURL("image/jpeg", 0.85); // 85% quality JPEG
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
          compress: true, // Enable PDF compression
        });

        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        // Add first page
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
        heightLeft -= pageHeight;

        // Add additional pages if content is longer than one page
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
          heightLeft -= pageHeight;
        }

        // Generate filename
        const invoiceId = `INV-${transaction._id?.slice(-8) || "UNKNOWN"}`;
        const fileName = `${invoiceId}_${moment(transaction.createdAt).format(
          "MMDDYYYY"
        )}.pdf`;

        // Download the PDF
        pdf.save(fileName);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <MyLoader />
      </div>
    );
  }

  if (!transaction || !leaseDetails || !userCompleteProfile?.personalInformation) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-gray-50">
        <p className="text-darkColor font-popinsSemiBold text-xl mb-4">
          Invoice data not available
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-themeColor text-white rounded-lg hover:bg-themeColor/90"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 py-8">
      <div className="max-w-[1200px] mx-auto px-4">
        {/* Header with Back and Download buttons */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-darkColor rounded-lg hover:bg-gray-50 transition-colors font-popinsMedium"
          >
            <ArrowLeft size={20} />
            Back
          </button>

          <button
            onClick={handleDownloadInvoice}
            disabled={downloading}
            className="flex items-center gap-2 px-6 py-2 bg-themeColor text-white rounded-lg hover:bg-themeColor/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-popinsSemiBold"
          >
            {downloading ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                Downloading...
              </>
            ) : (
              <>
                <Download size={20} />
                Download Invoice
              </>
            )}
          </button>
        </div>

        {/* Invoice Preview */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <TransactionInvoiceTemplate
            ref={invoiceRef}
            transaction={transaction}
            leaseDetails={leaseDetails}
            landlordInfo={userCompleteProfile.personalInformation}
          />
        </div>
      </div>
    </div>
  );
};

export default TransactionInvoice;
